function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useState, useMemo, useRef } from 'react';
import cn from 'classnames';
import { Callout, useOverlayContext } from 'seagull';
import { extractPureTextFromRichText, TextButton, Typography, Badge, Tooltip } from 'seagull';
import { EditLine } from 'seagull/icons';
import { ServicePriceEditor } from "../ServicePriceEditor/ServicePriceEditor";
import SimpleTextModal from "./SimpleTextModal";
import { getDiscountString, getPriceDisplayProps, getServiceDescriptionBadges, getStatusBadgeText, getPausedBadge } from "./ServiceDetails.utils";
import PriceDisplay from "./PriceDisplay";
import styles from "./ServiceDetails.module.scss";
import { serviceModel, serviceBillingModel } from "../../models";
import { renderBillingSettingsSection } from "./BillingSettingsSection";
import { getServiceBulletPoints } from "../../utils/service-description/getServiceBulletPoints";
import { useIoContext } from "../../contexts";
function ReadMoreButton(_ref) {
  let {
    service
  } = _ref;
  const {
    showModal
  } = useOverlayContext();
  return /*#__PURE__*/React.createElement(TextButton, {
    onClick: () => {
      showModal({
        closeOnClickOutside: true,
        closeOnEscapeKey: true,
        content: _ref2 => {
          let {
            dismissModal
          } = _ref2;
          return /*#__PURE__*/React.createElement(SimpleTextModal, {
            newDesign: true,
            title: service.name,
            text: service.description,
            onClose: () => dismissModal(true)
          });
        }
      });
    }
  }, "Read more");
}
export default function ServiceDetails(props) {
  const {
    service: rawService,
    testid,
    menu,
    agreementData,
    showBadges,
    showBulletPoints,
    additionalSections,
    isVendor,
    disableEditBillUpfront,
    disableEditBillingDay,
    disableStatusBadge = false,
    isReadOnly,
    ignoreAmendments,
    onCostChange,
    onHasError,
    onDayOfMonthChange,
    onDayOfWeekChange,
    onBillUpfrontModeChange
  } = props;
  const service = ignoreAmendments ? rawService : serviceModel.applyChanges(rawService);
  const isActiveAgreement = agreementData.isActiveAgreement();
  const {
    name,
    description,
    cost
  } = service;
  const {
    getNow
  } = useIoContext();
  const [isHovered, setIsHovered] = useState(false);
  const [priceEditorOpen, setPriceEditorOpen] = useState(false);
  const editorButtonRef = useRef(null);
  const [isDescriptionOverflowing, setIsDescriptionOverflowing] = useState(false);
  const pureTextDescription = useMemo(() => extractPureTextFromRichText(description), [description]);
  const statusBadgeText = getStatusBadgeText(service.status.type, isActiveAgreement, disableStatusBadge);
  const now = useMemo(() => getNow(), []);
  const badgesList = getServiceDescriptionBadges(service, isActiveAgreement, agreementData, now);
  const pausedBadge = getPausedBadge(service, isVendor, isActiveAgreement);
  const bulletPointsNEW = getServiceBulletPoints({
    service,
    now,
    agreementData,
    isReadOnly,
    testid,
    shouldRenderBillingBullet: !serviceBillingModel.isRepeatableManual(serviceModel.billingTrigger(service)),
    onDayOfWeekChange,
    onDayOfMonthChange,
    onBillUpfrontModeChange
  });
  function renderBulletPoint(_ref3) {
    let {
      text,
      idx,
      intent
    } = _ref3;
    return /*#__PURE__*/React.createElement("li", {
      key: idx,
      className: styles.listItem
    }, /*#__PURE__*/React.createElement("span", {
      className: styles.bullet
    }), /*#__PURE__*/React.createElement(Typography, {
      "data-testid": testid + "bullet-".concat(idx),
      "data-intent": intent
    }, text));
  }
  return /*#__PURE__*/React.createElement("div", {
    className: styles.serviceDetails,
    "data-testid": "".concat(testid, "service-details"),
    "data-serviceid": service.id
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.descriptionAndMenuButtonContainer
  }, /*#__PURE__*/React.createElement("div", {
    className: cn(styles.detailedDescription, {
      [styles.withBadge]: !!statusBadgeText
    })
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h6",
    ellipsis: true,
    "data-testid": testid + 'name'
  }, name), /*#__PURE__*/React.createElement("div", {
    className: styles.descriptionWrapper,
    "data-testid": "".concat(testid, "service-details-description")
  }, /*#__PURE__*/React.createElement(Typography, {
    ellipsis: true,
    hideTooltip: true,
    className: styles.description,
    ref: el => el && setIsDescriptionOverflowing(el.clientWidth < el.scrollWidth),
    skin: "secondary",
    "data-testid": testid + 'description'
  }, pureTextDescription), isDescriptionOverflowing && /*#__PURE__*/React.createElement(ReadMoreButton, {
    service: service
  }))), /*#__PURE__*/React.createElement("div", {
    className: styles.badgeAndDotMenuContainer
  }, pausedBadge && /*#__PURE__*/React.createElement(Tooltip, {
    title: pausedBadge.tooltip,
    placement: "top"
  }, /*#__PURE__*/React.createElement(Badge, {
    outlined: true,
    "data-testid": "service-details-paused-badge"
  }, "Paused")), statusBadgeText && /*#__PURE__*/React.createElement(Badge, {
    outlined: true,
    "data-testid": "".concat(testid, "status-badge")
  }, statusBadgeText), menu)), /*#__PURE__*/React.createElement("div", {
    className: styles.finalPriceContainerNEW
  }, onCostChange ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TextButton, {
    onClick: () => setPriceEditorOpen(true),
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
    disabled: isReadOnly,
    underline: "none",
    skin: "secondary",
    suffixIcon: /*#__PURE__*/React.createElement(EditLine, null),
    ref: editorButtonRef
  }, /*#__PURE__*/React.createElement(PriceDisplay, _extends({}, getPriceDisplayProps(service), {
    isInButton: isHovered || priceEditorOpen,
    "data-testid": testid + 'inline-edit-'
  }))), /*#__PURE__*/React.createElement(ServicePriceEditor, {
    cost: service.cost,
    editorAnchor: editorButtonRef.current,
    isOpen: priceEditorOpen,
    setIsOpen: setPriceEditorOpen,
    onCostChange: onCostChange
  })) : /*#__PURE__*/React.createElement(PriceDisplay, _extends({}, getPriceDisplayProps(service), {
    "data-testid": testid
  })), !showBadges && cost.discount && cost.discount.amount().greaterThan(0) && /*#__PURE__*/React.createElement("div", {
    "data-testid": testid + 'discount'
  }, /*#__PURE__*/React.createElement(Badge, {
    skin: "success"
  }, getDiscountString(service.cost.discount))), showBadges && badgesList.map(_ref4 => {
    let {
      text,
      intent,
      skin,
      tooltip
    } = _ref4;
    return /*#__PURE__*/React.createElement(Tooltip, {
      key: intent,
      title: tooltip || '',
      placement: "top"
    }, /*#__PURE__*/React.createElement(Badge, {
      rounded: true,
      outlined: true,
      skin: skin,
      "data-testid": testid + "badge-".concat(intent),
      "data-intent": intent
    }, text));
  })), showBulletPoints ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", null, bulletPointsNEW.bullets.map((text, idx) => renderBulletPoint({
    text,
    idx
  }))), bulletPointsNEW.pausedCallout && /*#__PURE__*/React.createElement(Callout, {
    className: styles.sectionNEW,
    withBorder: true
  }, bulletPointsNEW.pausedCallout)) : onDayOfWeekChange && onDayOfMonthChange && onBillUpfrontModeChange && renderBillingSettingsSection({
    testid,
    service,
    className: styles.section,
    isReadOnly,
    isActiveAgreement,
    disableBillUpfront: disableEditBillUpfront,
    disableBillingDay: disableEditBillingDay,
    shouldRenderBillingBullet: !serviceBillingModel.isRepeatableManual(serviceModel.billingTrigger(service)),
    onHasError,
    onDayOfWeekChange,
    onDayOfMonthChange,
    onBillUpfrontModeChange
  }), additionalSections && additionalSections.map((_ref5, idx) => {
    let {
      value,
      disableBorder
    } = _ref5;
    return value && /*#__PURE__*/React.createElement("div", {
      className: cn(styles.additionalSection, disableBorder && styles.disableBorder),
      key: idx
    }, value);
  }));
}